import { useCallback, useEffect, useId, useRef, useState } from "react";
import { api } from "../api/client";
import type { AuthSession, CustomerQuoteSmsOmission, DeviceDeliveryChannelStatus, DeviceDeliveryMonitoringView } from "../api/types";
import { errorMessage } from "../utils/errors";
import { formatDateTime } from "../utils/format";

const CHANNEL_LABELS: Record<DeviceDeliveryChannelStatus["channel"], string> = { kakao: "카카오 발송기", sms: "문자 발송기" };
const MONITORING_REQUEST_TIMEOUT_MS = 10_000;
export const DELIVERY_MONITORING_UPDATED = "selmo:delivery-monitoring-updated";

export function channelProblemText(channel: DeviceDeliveryChannelStatus): string {
  if (channel.reason === "delivery_failed") {
    const rooms = channel.rooms?.filter(room => !room.sessionReady).map(room => room.room) ?? [];
    return rooms.length ? `채팅방 연결 확인 필요 · ${rooms.join(" · ")}` : "발송 실패가 이어지고 있습니다. 연결과 발송 결과를 확인해주세요.";
  }
  return `${Math.round(channel.staleAfterSeconds / 60)}분 이상 무신호`;
}

export function staleDeviceAlertText(channels: DeviceDeliveryChannelStatus[]): string {
  return channels.filter(channel => channel.status === "stale")
    .map(channel => `${CHANNEL_LABELS[channel.channel]} ${channelProblemText(channel)}`).join(" · ");
}

export function deviceDeliveryAlertContent(channels: DeviceDeliveryChannelStatus[], monitoringUnavailable: boolean): { text: string; detail: string } | null {
  if (monitoringUnavailable) return {
    text: "발송기 상태를 조회하지 못했습니다.",
    detail: "연결을 확인한 뒤 다시 조회해주세요. 현재 발송기 상태는 확인되지 않았습니다.",
  };
  const text = staleDeviceAlertText(channels);
  return text ? { text, detail: "확인 후에도 장애 상태는 복구될 때까지 표시됩니다." } : null;
}

export function DeviceDeliveryAlertBanner({ session }: { session: AuthSession }) {
  const [monitoring, setMonitoring] = useState<DeviceDeliveryMonitoringView>();
  const [monitoringUnavailable, setMonitoringUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const requestSequence = useRef(0);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (session.role !== "ADMIN") return;
    const sequence = ++requestSequence.current;
    const requestController = new AbortController();
    const abortRequest = () => requestController.abort();
    signal?.addEventListener("abort", abortRequest, { once: true });
    const timeout = window.setTimeout(abortRequest, MONITORING_REQUEST_TIMEOUT_MS);
    try {
      const result = await api.deviceDeliveryMonitoring(requestController.signal);
      if (signal?.aborted || sequence !== requestSequence.current) return;
      setMonitoring(result);
      setMonitoringUnavailable(!result.channels?.length);
    } catch {
      if (signal?.aborted || sequence !== requestSequence.current) return;
      setMonitoringUnavailable(true);
    } finally {
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", abortRequest);
    }
  }, [session.role]);

  useEffect(() => {
    if (session.role !== "ADMIN") return;
    const controller = new AbortController();
    void load(controller.signal);
    const timer = window.setInterval(() => void load(), 60_000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [load, session.role]);

  async function acknowledge(request: () => Promise<DeviceDeliveryMonitoringView>) {
    ++requestSequence.current;
    setBusy(true);
    setError(undefined);
    try {
      const result = await request();
      ++requestSequence.current;
      setMonitoring(result);
      setMonitoringUnavailable(!result.channels?.length);
      window.dispatchEvent(new CustomEvent(DELIVERY_MONITORING_UPDATED, { detail: result }));
    } catch (cause) { setError(errorMessage(cause)); }
    finally { setBusy(false); }
  }

  if (session.role !== "ADMIN") return null;
  const customerQuotes = monitoring?.customerQuoteSms;
  const unavailable = deviceDeliveryAlertContent([], monitoringUnavailable);
  return <>
    {unavailable ? <div className="device-delivery-alert device-delivery-unavailable" role="alert" data-feedback-id="device-delivery.unavailable">
      <strong>발송기 상태 조회 오류</strong><span>{unavailable.text}</span><small>{unavailable.detail}</small>
      <button className="text-button" type="button" onClick={() => void load()}>다시 조회</button>
    </div> : monitoring?.channels.filter(channel => channel.status === "stale").map(channel =>
      <DeviceDeliveryChannelAlert key={`${channel.channel}:${channel.activeIncidentId}`} channel={channel} busy={busy}
        onAcknowledge={() => channel.activeIncidentId && void acknowledge(() => api.acknowledgeDeviceDelivery(channel.channel, channel.activeIncidentId!))} />)}
    {error && <div className="form-error" role="alert">확인 상태를 저장하지 못했습니다. {error}</div>}
    {customerQuotes?.items.length ? <CustomerQuoteSmsAlert items={customerQuotes.items} busy={busy}
      alertRecipientAvailable={customerQuotes.alertRecipientAvailable}
      onAcknowledge={item => void acknowledge(() => api.acknowledgeCustomerQuoteSms([{ quoteId: item.quoteId, approvedAt: item.approvedAt }]))} /> : null}
  </>;
}

export function DeviceDeliveryChannelAlert({ channel, busy, onAcknowledge }: {
  channel: DeviceDeliveryChannelStatus; busy: boolean; onAcknowledge: () => void;
}) {
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const detailsId = useId();
  const acknowledged = Boolean(channel.acknowledgment);
  const expanded = !acknowledged || manuallyExpanded;
  return <section className="device-delivery-alert device-delivery-channel" role={acknowledged ? "status" : "alert"}
    data-feedback-id={`device-delivery.${channel.channel}`} aria-label={`${CHANNEL_LABELS[channel.channel]} 장애`}>
    <div className="customer-quote-sms-alert__summary">
      <strong>{CHANNEL_LABELS[channel.channel]} · 장애 지속{acknowledged ? " · 확인 완료" : " · 확인 필요"}</strong>
      <div className="customer-quote-sms-alert__actions">
        {acknowledged ? <button type="button" aria-expanded={expanded} aria-controls={detailsId}
          onClick={() => setManuallyExpanded(value => !value)}>{expanded ? "상세 접기" : "상세 보기"}</button>
          : <button type="button" disabled={busy || !channel.activeIncidentId} onClick={onAcknowledge}>확인하고 접기</button>}
      </div>
    </div>
    <div id={detailsId} className="customer-quote-sms-alert__details" hidden={!expanded}>
      <span>{channelProblemText(channel)}</span>
      <small>{channel.lastHeartbeatAt ? `마지막 신호 ${formatDateTime(channel.lastHeartbeatAt)}` : "수신된 발송기 신호가 없습니다."}</small>
      {channel.acknowledgment && <small>{channel.acknowledgment.acknowledgedBy} · {formatDateTime(channel.acknowledgment.acknowledgedAt)} 확인</small>}
      <small>확인은 장애 복구 처리가 아닙니다. 새 장애가 발생하면 다시 펼쳐집니다.</small>
    </div>
  </section>;
}

export function CustomerQuoteSmsAlert({ items, alertRecipientAvailable, onAcknowledge, busy = false }: {
  items: CustomerQuoteSmsOmission[]; alertRecipientAvailable: boolean;
  onAcknowledge?: (item: CustomerQuoteSmsOmission) => void; busy?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  return <div className="device-delivery-alert customer-quote-sms-alert" role="alert" data-feedback-id="customer-quote-sms.alert">
    <div className="customer-quote-sms-alert__summary">
      <strong>고객 문자 확인 필요 · {items.length}건</strong>
      <div className="customer-quote-sms-alert__actions">
        <a href="#/sms">문자함 확인</a>
        <button type="button" aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(current => !current)}>{expanded ? "상세 접기" : "상세 보기"}</button>
      </div>
    </div>
    <div id={detailsId} className="customer-quote-sms-alert__details" hidden={!expanded}>
      <ul>{items.map(item => <li key={`${item.quoteId}:${item.approvedAt}`}>
        <a href={`#/projects?project=${encodeURIComponent(item.projectId)}&tab=quotes&quote=${encodeURIComponent(item.quoteId)}`}>{item.customerName}님 · {item.quoteVersion}차 견적</a>
        <span>{item.reasonLabel}</span>
        {onAcknowledge && <button className="text-button" type="button" disabled={busy} onClick={() => onAcknowledge(item)}>재발송 안 함·확인 완료</button>}
      </li>)}</ul>
      <small>{alertRecipientAvailable
        ? "확인 완료한 건은 재발송하지 않고 문자함의 확인 이력에 보관합니다."
        : "셀모의 알림 수신번호를 확인하지 못해 문자 안내를 보낼 수 없습니다. 미해결 알림은 계속 표시됩니다."}</small>
    </div>
  </div>;
}
